import React, { useEffect, useRef, useState } from 'react'
// import InputText from '../../utils/controls/input-text'
// import InputCheck from '../../utils/controls/input-checkbox'
// import Buttons from '../../components/buttons/buttons'
// import CustomSelect from '../../utils/controls/custom-select'
import { RhyBusInputText, RhyBusCustomSelect, RhyBusInputCheck } from '@rhybus/form-controls'
import { RhyBusButton } from '@rhybus/button'
import { tooltipDirection, typeOptions, iconsList } from './element-options-list'
import { metadataDefinitions } from '../services/metadataManagement'

const metaDataTypeOptions = metadataDefinitions.map(d => ({ value: d.metaDataType, label: d.title }))

interface StaticOption { label: string; value: string }

interface ChildItem {
  id: string | null; label: string | null; internalName: string | null; type: string | null; isInfo: boolean | null; isMandatory: boolean | null; position: string | null; placeholder: string | null; readonly: boolean | null; disabled: boolean | null;
  metaDataType?: string | null;
  options?: StaticOption[];
  className?: string; bgColor?: string; cbFontColor?: string; cbBorder?: string; cbFontSize?: string; cbFontWeight?: string; cbPadding?: string; cbBorderRadius?: string;
  buttonPosition?: string; checkPosition?: string; leftIcon?: string; rightIcon?: string; size?: string;
  variant?: string; color?: string; buttonType?: string; pill?: boolean; buttonClassName?: string;
  firstLabel?: string; secondLabel?: string;
  // RhyBusInputText-specific
  inputSize?: string; maxLength?: number; min?: number; max?: number; formlayout?: string;
  iconLeft?: boolean; iconRight?: boolean; customIconLeft?: string; customIconRight?: string; customIcon?: string;
  currency?: string; decimalPlaces?: number;
  inlineType?: string; isBadge?: boolean; maxCount?: number; inline?: boolean; autoComplete?: boolean;
  isMulti?: boolean;
  isGroup?: boolean;
  mode?: string;
  minDate?: string;
  maxDate?: string;
  displayText?: string;
  rows?: number;
  hint?: string;
  note?: string;
  chooseFileLabel?: string;
  maxFiles?: number;
  maxFileSizeMB?: number;
  acceptedTypes?: string;
  removeMessage?: string;
  replaceMessage?: string;
}

interface ElementOptionsProps {
  child?: Array<ChildItem>;
  onUpdate: (updatedChild: Array<ChildItem>, containerId: string, childId: string, rowId: string) => void;
  onClicked?: any
  selectChild?: any
  addradio?: boolean;
  containers?: any
  containerId?: any,
  selectedRowId: string | undefined
}

const COLOR_SWATCHES = [
  '#ffffff', '#000000', '#ff6600',
  '#4d94ff', '#33cc33', '#0052cc',
  '#003399', '#66b2ff', '#006644', '#ffcc00',
  '#333333', '#9933ff', '#ff33aa',
  '#666666', '#cccccc', '#e6e6e6',
]

const swatchOptions = COLOR_SWATCHES.map(hex => ({
  value: hex,
  label: <span style={{ display: 'block', width: 18, height: 18, borderRadius: 3, backgroundColor: hex, border: '1px solid rgba(0,0,0,0.15)' }} />,
}))
const CUSTOM_SWATCH = {
  value: '__custom__',
  label: <span style={{ display: 'block', width: 18, height: 18, borderRadius: 3, background: 'linear-gradient(135deg,red,orange,yellow,green,blue,violet)', border: '1px solid rgba(0,0,0,0.15)' }} title="Custom" />,
}
const allSwatchOptions = [...swatchOptions, CUSTOM_SWATCH]

const swatchMenuListStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4, padding: 8, width: '100%' }
const swatchOptionStyle = (isSelected: boolean): React.CSSProperties => ({
  width: 26, height: 26, padding: 0, cursor: 'pointer', borderRadius: 4, boxSizing: 'border-box',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: isSelected ? '1px solid var(--rb-primary-color)' : '1px solid transparent',
  background: 'none',
})
const swatchComponents = {
  MenuList: ({ children, innerRef, innerProps }: any) => <div ref={innerRef} {...innerProps} style={swatchMenuListStyle}>{children}</div>,
  Option: ({ children, innerRef, innerProps, isSelected }: any) => <div ref={innerRef} {...innerProps} style={swatchOptionStyle(isSelected)}>{children}</div>,
}

const ColorSwatchPicker: React.FC<{ label: string; id: string; value: string; onChange: (v: string) => void }> = ({ label, id, value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const isCustom = value && !COLOR_SWATCHES.includes(value)
  const selectedOption = isCustom
    ? { value, label: <span style={{ display: 'block', width: 18, height: 18, borderRadius: 3, backgroundColor: value, border: '1px solid rgba(0,0,0,0.15)' }} /> }
    : (swatchOptions.find(o => o.value === value) || null)

  const handleChange = (opt: any) => {
    if (opt?.value === '__custom__') {
      inputRef.current?.click()
    } else {
      onChange(opt?.value || '')
    }
  }

  return (
    <div className='mb-1' style={{ position: 'relative' }}>
      <RhyBusCustomSelect
        id={id}
        label={label}
        options={allSwatchOptions}
        value={selectedOption}
        onChange={handleChange}
        menuPortalTarget={document.body}
        components={swatchComponents}
      />
      <input
        ref={inputRef}
        type="color"
        value={value || '#000000'}
        style={{ width: 0, height: 0, opacity: 0, position: 'absolute', pointerEvents: 'none' }}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

const iconOptions = [
  { value: '', label: 'None' },
  ...iconsList.map((icon: any) => ({ value: icon.value as string, label: icon.value as string })),
]
const iconMenuComponents = {
  MenuList: ({ children, innerRef, innerProps }: any) => (
    <div ref={innerRef} {...innerProps} style={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: 8, maxHeight: 220, overflowY: 'auto' }}>
      {children}
    </div>
  ),
  Option: ({ data, innerRef, innerProps, isSelected }: any) => (
    <div
      ref={innerRef}
      {...innerProps}
      title={data.value || 'None'}
      style={{
        width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', borderRadius: 4, fontSize: 16, boxSizing: 'border-box',
        border: isSelected ? '2px solid #0052cc' : '1px solid transparent',
        background: isSelected ? '#EEF0FF' : 'none',
      }}
    >
      {data.value ? <span className={data.value} /> : <span style={{ fontSize: 10, color: '#aaa' }}>∅</span>}
    </div>
  ),
  SingleValue: ({ data, innerProps }: any) => (
    <div {...(innerProps || {})} style={{ gridArea: '1/1/2/3', display: 'flex', alignItems: 'center', maxWidth: '100%', overflow: 'hidden' }}>
      {data?.value
        ? <span className={`${data.value} font-14`} />
        : <span style={{ fontSize: 11, color: '#999' }}>None</span>
      }
    </div>
  ),
}

const IconPickerSelect: React.FC<{ label: string; id: string; value: string; onChange: (v: string) => void }> = ({ label, id, value, onChange }) => {
  const selectedOption = iconOptions.find(o => o.value === value) ?? iconOptions[0]
  return (
    <RhyBusCustomSelect
      id={id}
      label={label}
      options={iconOptions}
      value={selectedOption}
      onChange={(v: any) => onChange(v?.value || '')}
      components={iconMenuComponents}
      menuPortalTarget={document.body}
      menuPlacement="auto"
      styles={{ menu: (base: any) => ({ ...base, width: 248, minWidth: 248 }) }}
    />
  )
}

const cbFontSizeOptions = [
  { value: '12px', label: '12px' }, { value: '14px', label: '14px' }, { value: '16px', label: '16px' },
  { value: '18px', label: '18px' }, { value: '20px', label: '20px' }, { value: '24px', label: '24px' },
  { value: '28px', label: '28px' }, { value: '32px', label: '32px' },
]
const cbFontWeightOptions = [
  { value: '400', label: 'Normal' }, { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi Bold' }, { value: '700', label: 'Bold' },
]

const switchSizeOptions = [
  { value: 'xs', label: 'Extra Small' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
]

const buttonSizeOptions = [
  { value: 'default', label: 'Default' },
  { value: 'xs', label: 'Extra Small' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
]

const buttonVariantOptions = [
  { value: 'primary',   label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'danger',    label: 'Danger' },
  { value: 'ghost',     label: 'Ghost' },
  { value: 'text',      label: 'Text' },
]

const buttonColorOptions = [
  { value: 'primary', label: 'Primary' },
  { value: 'danger',  label: 'Danger' },
  { value: 'success', label: 'Success' },
  { value: 'warning', label: 'Warning' },
  { value: 'purple',  label: 'Purple' },
]

const buttonTypeOptions = [
  { value: 'button', label: 'Button' },
  { value: 'submit', label: 'Submit' },
  { value: 'reset',  label: 'Reset' },
]


const formLayoutOptions = [
  { value: 'vertical',   label: 'Vertical' },
  { value: 'horizontal', label: 'Horizontal' },
]

const dateModeOptions = [
  { value: 'date',      label: 'Date' },
  { value: 'datetime',  label: 'Date & Time' },
  { value: 'range',     label: 'Date Range' },
]

const currencyOptions = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'INR', label: 'INR (₹)' },
  { value: 'EUR', label: 'EUR (€)' },
]

const inlineTypeOptions = [
  { value: 'text', label: 'Text (Inline)' },
  { value: 'pill', label: 'Pill (Inline)' },
  { value: 'sup', label: 'Sup' },
]

const buttonPositionOptions = [
  { value: 'left',          label: 'Left' },
  { value: 'center',        label: 'Center' },
  { value: 'right',         label: 'Right' },
  { value: 'top-left',      label: 'Top Left' },
  { value: 'top-center',    label: 'Top Center' },
  { value: 'top-right',     label: 'Top Right' },
  { value: 'center-left',   label: 'Center Left' },
  { value: 'center-right',  label: 'Center Right' },
  { value: 'bottom-left',   label: 'Bottom Left' },
  { value: 'bottom-center', label: 'Bottom Center' },
  { value: 'bottom-right',  label: 'Bottom Right' },
]

export const getButtonPositionClass = (pos?: string): string => {
  const map: Record<string, string> = {
    'left':           'd-flex justify-content-start',
    'center':         'd-flex justify-content-center',
    'right':          'd-flex justify-content-end',
    'top-left':       'd-flex justify-content-start',
    'top-center':     'd-flex justify-content-center',
    'top-right':      'd-flex justify-content-end',
    'center-left':    'd-flex justify-content-start my-auto w-100',
    'center-right':   'd-flex justify-content-end my-auto w-100',
    'bottom-left':    'd-flex justify-content-start mt-auto w-100',
    'bottom-center':  'd-flex justify-content-center mt-auto w-100',
    'bottom-right':   'd-flex justify-content-end mt-auto w-100',
  }
  return pos ? (map[pos] || '') : ''
}

export const getButtonColClass = (pos?: string): string =>
  pos?.startsWith('top-') || pos?.startsWith('center-') || pos?.startsWith('bottom-') ? 'd-flex flex-column' : ''

export const buildUploadHint = (acceptedTypes?: string, maxFileSizeMB?: number, maxFiles?: number): string => {
  const types = acceptedTypes
    ? acceptedTypes.split(',').map(s => s.trim()).filter(Boolean).join(', ')
    : '.png, .jpeg, .xlsx, .txt, .docx, .pdf'
  const size = maxFileSizeMB != null ? `${maxFileSizeMB} MB` : '25 MB'
  const filesLimit = maxFiles != null ? ` Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed.` : ' Limit is up to 10 files per screen.'
  return `Upload the file which are in the ${types} files and special characters will not be used in the document names. Maximum size for file is ${size}.${filesLimit}`
}
export const formatPhoneNumber = (raw: string): string => {
  const isIntl = raw.startsWith('+')
  const digits = raw.replace(/\D/g, '')
  if (!digits) return isIntl ? '+' : ''
  if (isIntl) {
    const cc = digits[0]
    const rest = digits.slice(1)
    if (!rest.length) return `+${cc}`
    if (rest.length <= 3) return `+${cc} (${rest}`
    if (rest.length <= 6) return `+${cc} (${rest.slice(0, 3)}) ${rest.slice(3)}`
    return `+${cc} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6, 10)}`
  }
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
}

const ElementOptions: React.FC<ElementOptionsProps> = (props) => {
  const { child, onUpdate, selectChild, addradio, containerId, selectedRowId } = props
  const [children, setChildren] = useState<Array<ChildItem> | null>(null)
  const [applyStatus, setApplyStatus] = useState<'idle' | 'applied'>('idle')
  const baselineRef = useRef<string>('')

  useEffect(() => {
    if (child && child?.length > 0) {
      setChildren(child)
      // Reset baseline and status whenever the selected field changes
      const current = child.find((c: any) => c.id === selectChild)
      baselineRef.current = JSON.stringify(current ?? null)
      setApplyStatus('idle')
    }
  }, [child, selectChild])

  const handleLabelChange = (newLabel: string, id: number) => {
    if (children) {
      const updatedChildren = children.map((child: any) =>
        child.id === id
          ? { ...child, label: newLabel } // Update the label of the selected child
          : child // Keep other children unchanged
      )
      setChildren(updatedChildren) // Update the state with the modified children
    }
  }

  const handlePlaceholderChange = (newPlaceholder: string, id: number) => {
    if (children) {
      const updatedChildren = children.map((child: any) =>
        child.id === id
          ? { ...child, placeholder: newPlaceholder } // Update the label of the selected child
          : child // Keep other children unchanged
      )
      setChildren(updatedChildren)
    }

  }
const handleInternalNameChange = (newInternalName: string, id: number) => {
    if (children) {
      const updatedChildren = children.map((child: any) =>
        child.id === id
          ? { ...child, internalName: newInternalName }
          : child
      )
      setChildren(updatedChildren)
    }
  }
  const onchangeType = (e: any, id: number) => {
    const fieldValue = e?.value ?? e?.target?.value ?? e ?? null

    setChildren((prevChildren: any) =>
      prevChildren.map((child: any) =>
        child.id === id
          ? {
            ...child, type: fieldValue,
          }
          : child
      )
    )
  }

  const selectedChildSnapshot = JSON.stringify(children?.find((c: any) => c.id === selectChild) ?? null)
  const isDirty = selectedChildSnapshot !== baselineRef.current

  const handleApply = () => {
    if (children) {
      onUpdate(children, containerId, selectChild, selectedRowId as string)
      baselineRef.current = selectedChildSnapshot
      setApplyStatus('applied')
      setTimeout(() => setApplyStatus('idle'), 2000)
    }
  }

  const handleClear = () => {
    if (children) {
      const clearedChildren = children.map((child) => ({
        ...child,
        label: '',
        placeholder: '',
        internalName: '',
        readonly: false,
        disabled: false
      }))
      setChildren(clearedChildren)
    }
  }

  // const [isChecked, setIsChecked] = useState(selectChild?.isInfo || false)
  // const [readOnly, setReadOnly] = useState(selectChild?.readonly || false)
  // const [readWrite, setReadWrite] = useState('')
  // const [disable, setDisable] = useState(selectChild?.disable || false)
  // const [isMandatoryChecked, setIsMandatoryChecked] = useState(selectChild?.isMandatory || false)
  // const [isClicked, setIsClicked] = useState(false)
  // const [clickedId, setClickedId] = useState<number | null>(null)
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    if (children) {
      const updatedChildren = children.map((child: any) =>
        child.id === id
          ? { ...child, isInfo: event.target.checked } // Update the label of the selected child
          : child // Keep other children unchanged
      )
      setChildren(updatedChildren)
    }

  }

  const handleMandatoryCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    if (children) {
      const updatedChildren = children.map((child: any) =>
        child.id === id
          ? { ...child, isMandatory: event.target.checked } // Update the label of the selected child
          : child // Keep other children unchanged
      )
      setChildren(updatedChildren)
    }

  }

  // const addElement = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
  //   event.preventDefault() // Prevent any default action
  //   if (id) {
  //     setIsClicked(true)
  //     setClickedId(id)
  //   }
  //   props.onClicked(id)
  // }
  const handlePositionChange = (e: any, id: number) => {
    const newPosition = e?.value || ''
    setChildren((prevChildren: any) =>
      prevChildren.map((child: any) =>
        child.id === id
          ? { ...child, position: newPosition }
          : child
      )
    )
  }

  const handleMetaDataTypeChange = (e: any, id: number) => {
    const newType = e?.value ?? null
    setChildren((prev: any) => prev?.map((c: any) => c.id === id ? { ...c, metaDataType: newType } : c))
  }

  const handleAddOption = (id: number) => {
    setChildren((prev: any) => prev?.map((c: any) =>
      c.id === id ? { ...c, options: [...(c.options ?? []), { label: '', value: '' }] } : c
    ))
  }

  const handleRemoveOption = (id: number, idx: number) => {
    setChildren((prev: any) => prev?.map((c: any) =>
      c.id === id ? { ...c, options: (c.options ?? []).filter((_: any, i: number) => i !== idx) } : c
    ))
  }

  const handleOptionChange = (id: number, idx: number, field: 'label' | 'value', val: string) => {
    setChildren((prev: any) => prev?.map((c: any) => {
      if (c.id !== id) return c
      const updated = (c.options ?? []).map((o: StaticOption, i: number) =>
        i === idx ? { ...o, [field]: val, ...(field === 'label' ? { value: val } : {}) } : o
      )
      return { ...c, options: updated }
    }))
  }

  const handleCbStyleChange = (field: keyof ChildItem, value: any, id: number) => {
    setChildren((prev: any) => prev?.map((c: any) => c.id === id ? { ...c, [field]: value } : c))
  }

  const handleMultiCbStyleChange = (fields: Record<string, any>, id: number) => {
    setChildren((prev: any) => prev?.map((c: any) => c.id === id ? { ...c, ...fields } : c))
  }

  const handlePropertyChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    id: number,
    property: 'readonly' | 'readandwrite'
  ) => {
    if (children) {
      const updatedChildren = children.map((child: any) =>
        child.id === id
          ? property === 'readandwrite'
            ? { ...child, readonly: false, disabled: false }
            : { ...child, readonly: true, disabled: false }
          : child
      )
      setChildren(updatedChildren)
    }
  }

  const selectedChild = children?.find((item: any) => item.id === selectChild)
  const fieldType = selectedChild?.type || ''
  const isContentBlock = selectedChild?.className === 'content-block-item'

  const isButton = fieldType === 'Button'
  const isSwitch = fieldType === 'Switch'
  const isPeoplePicker = fieldType === 'PeoplePicker'
  const isTextLike = !isContentBlock && ['Input', 'text', 'email', 'number', 'phone', 'search', 'currency'].includes(fieldType)
  const isSelect = fieldType === 'Select'
  const isTextarea = fieldType === 'Textarea'
  const isCheckRadio = ['Radio', 'radio', 'checkbox'].includes(fieldType)
  const isDatePicker = fieldType === 'DatePicker'
  const isUpload = fieldType === 'uploadfile'
  const isRichText = fieldType === 'RichText'

  const showPlaceholder = isTextLike || isSelect || isTextarea || isDatePicker || isRichText
  const showType = isTextLike || isCheckRadio
  const showIsInfo = !isButton
  const showTooltip = !isButton && !!selectedChild?.isInfo
  const showAccess = !isSwitch && !isPeoplePicker && !isUpload && !isRichText
  const showMandatory = !isButton

  const fieldTypeLabel: Record<string, string> = {
    Input: 'Text Field', text: 'Text Field', email: 'Text Field', number: 'Text Field', phone: 'Text Field', search: 'Text Field', currency: 'Text Field',
    Select: 'Custom Select', Textarea: 'Textarea',
    Radio: 'Radio / Checkbox', radio: 'Radio / Checkbox', checkbox: 'Radio / Checkbox',
    DatePicker: 'Date Picker', uploadfile: 'Upload File',
    Button: 'Button', PeoplePicker: 'People Picker', Switch: 'Switch',
    SectionHeader: 'Section Header', SubSectionHeader: 'Sub Section Header',
    ContractDurationHeader: 'Contract Duration Header',
    RichText: 'Rich Text Editor',
  }

  return (
    <div className="element-options gap-2 d-flex flex-column pb-2">
      {!selectedChild ? (
        <p className='font-12 color-secondary mt-2'>Select a field to edit its properties.</p>
      ) : (
        <React.Fragment key={selectChild}>
          <h6 className='border-bottom py-2 mb-2 color-primary font-13'>
            {isContentBlock ? 'Content Block Properties' : (fieldTypeLabel[fieldType] || fieldType) + ' Properties'}
          </h6>

          {/* Content Block styling controls */}
          {isContentBlock && (
            <>
              <ColorSwatchPicker
                id='cb-bgcolor'
                label="BG Color"
                value={selectedChild?.bgColor || ''}
                onChange={(v: string) => handleCbStyleChange('bgColor', v, selectChild)}
              />
              <ColorSwatchPicker
                id='cb-fontcolor'
                label="Font Color"
                value={selectedChild?.cbFontColor || ''}
                onChange={(v: string) => handleCbStyleChange('cbFontColor', v, selectChild)}
              />
              <RhyBusInputText
                id='cb-border'
                label='Border'
                className='font-12'
                placeholder='e.g. 1px solid #ccc'
                value={selectedChild?.cbBorder || ''}
                onChange={(e: any) => handleCbStyleChange('cbBorder', e.target.value, selectChild)}
              />
              <RhyBusCustomSelect
                id='cb-fontsize'
                label='Font Size'
                options={cbFontSizeOptions}
                value={cbFontSizeOptions.find(o => o.value === selectedChild?.cbFontSize) || null}
                onChange={(v: any) => handleCbStyleChange('cbFontSize', v?.value || '', selectChild)}
              />
              <RhyBusCustomSelect
                id='cb-fontweight'
                label='Font Weight'
                options={cbFontWeightOptions}
                value={cbFontWeightOptions.find(o => o.value === selectedChild?.cbFontWeight) || null}
                onChange={(v: any) => handleCbStyleChange('cbFontWeight', v?.value || '', selectChild)}
              />
              <RhyBusInputText
                id='cb-padding'
                label='Padding'
                className='font-12'
                placeholder='e.g. 8px 12px'
                value={selectedChild?.cbPadding || ''}
                onChange={(e: any) => handleCbStyleChange('cbPadding', e.target.value, selectChild)}
              />
              <RhyBusCustomSelect
                id='cb-borderradius'
                label='Border Radius'
                options={[
                  { value: '4px', label: '4px' },
                  { value: '8px', label: '8px' },
                  { value: '12px', label: '12px' },
                  { value: '16px', label: '16px' },
                  { value: '20px', label: '20px' },
                  { value: '50%', label: '50%' },
                ]}
                value={selectedChild?.cbBorderRadius ? { value: selectedChild.cbBorderRadius, label: selectedChild.cbBorderRadius } : null}
                onChange={(v: any) => handleCbStyleChange('cbBorderRadius', v?.value || '', selectChild)}
              />
            </>
          )}

          {/* Regular form field properties — hidden for content blocks */}
          {!isContentBlock && (
            <>
              <RhyBusInputText
                id='changelabelname'
                name='Label name'
                className='font-12'
                placeholder='Label Name'
                maxLength={255}
                value={selectedChild.label || ''}
                label='Label name'
                onChange={(e: any) => handleLabelChange(e.target.value, selectChild)}
              />
              <RhyBusInputText
                id='changeinternalname'
                name='Internal Name'
                className='font-12'
                placeholder='e.g. fiscalYear'
                maxLength={100}
                value={selectedChild.internalName || ''}
                label='Internal Name'
                onChange={(e: any) => handleInternalNameChange(e.target.value, selectChild)}
              />
              {showPlaceholder && (
                <RhyBusInputText
                  id='changeplaceholdername'
                  name='Placeholder'
                  className='font-12'
                  placeholder={selectedChild.label ? `Enter ${selectedChild.label}` : 'Enter placeholder name'}
                  maxLength={255}
                  value={selectedChild.placeholder || (selectedChild.label ? `Enter ${selectedChild.label}` : '')}
                  label='Placeholder'
                  onChange={(e: any) => handlePlaceholderChange(e.target.value, selectChild)}
                />
              )}
              {isSelect && (
                <>
                  <RhyBusCustomSelect
                    id="metadatasource"
                    label="Metadata Source"
                    options={metaDataTypeOptions}
                    value={metaDataTypeOptions.find(o => o.value === selectedChild?.metaDataType) || null}
                    onChange={(e: any) => handleMetaDataTypeChange(e, selectChild)}
                    isClearable
                  />
                  {/* Static options — only shown when no Metadata Source is selected */}
                  {!selectedChild.metaDataType && (
                    <div className="d-flex flex-column gap-1 mt-1">
                      <label className="">Static Options</label>
                      {(selectedChild.options ?? []).map((opt: StaticOption, idx: number) => (
                        <div key={idx} className="d-flex gap-1 align-items-center">
                          <RhyBusInputText
                            id={`opt-label-${idx}`}
                            placeholder="Option label"
                            value={opt.label}
                            className="font-12"
                            onChange={(e: any) => handleOptionChange(selectChild, idx, 'label', e.target.value)}
                          />
                          <span
                            role="button"
                            className="icon-rb-trashbin color-danger font-14 cursor-pointer"
                            onClick={() => handleRemoveOption(selectChild, idx)}
                          />
                        </div>
                      ))}
                      <RhyBusButton
                        label="+ Add Option"
                        variant="secondary"
                        size="sm"
                        className="font-12 mt-1"
                        onClick={() => handleAddOption(selectChild)}
                      />
                    </div>
                  )}
                  <RhyBusCustomSelect
                    id="select-formlayout"
                    label="Form Layout"
                    options={formLayoutOptions}
                    value={formLayoutOptions.find(o => o.value === ((selectedChild as any)?.formlayout ?? 'vertical')) || formLayoutOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('formlayout', v?.value ?? 'vertical', selectChild)}
                  />
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id="select-ismulti"
                      name="isMulti"
                      type="checkbox"
                      label="Multi Select"
                      checked={!!(selectedChild as any)?.isMulti}
                      onChange={(e: any) => handleCbStyleChange('isMulti', e.target.checked, selectChild)}
                    />
                    <RhyBusInputCheck
                      id="select-isbadge"
                      name="isBadge"
                      type="checkbox"
                      label="Is Badge"
                      checked={!!(selectedChild as any)?.isBadge}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        handleMultiCbStyleChange({ isBadge: checked, inline: checked, inlineType: checked ? ((selectedChild as any)?.inlineType || 'pill') : undefined, maxCount: checked ? (selectedChild as any)?.maxCount : undefined }, selectChild)
                      }}
                    />
                  </div>
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusCustomSelect
                      id="select-inlinetype"
                      label="Badge Style"
                      options={inlineTypeOptions}
                      value={inlineTypeOptions.find(o => {
                        if (!(selectedChild as any)?.inline) return o.value === 'sup'
                        return o.value === ((selectedChild as any)?.inlineType ?? 'pill')
                      }) || inlineTypeOptions[0]}
                      onChange={(v: any) => {
                        const val = v?.value ?? 'pill'
                        handleMultiCbStyleChange({ inlineType: val === 'sup' ? undefined : val, inline: val !== 'sup' }, selectChild)
                      }}
                    />
                  )}
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusInputText
                      id="select-maxcount"
                      label="Max Count"
                      type="number"
                      placeholder="e.g. 100"
                      value={String((selectedChild as any)?.maxCount ?? '')}
                      onChange={(e: any) => handleCbStyleChange('maxCount', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                    />
                  )}
                </>
              )}
              {isDatePicker && (
                <>
                  <RhyBusCustomSelect
                    id="date-mode"
                    label="Mode"
                    options={dateModeOptions}
                    value={dateModeOptions.find(o => o.value === ((selectedChild as any)?.mode ?? 'date')) || dateModeOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('mode', v?.value ?? 'date', selectChild)}
                  />
                  <RhyBusCustomSelect
                    id="date-formlayout"
                    label="Form Layout"
                    options={formLayoutOptions}
                    value={formLayoutOptions.find(o => o.value === ((selectedChild as any)?.formlayout ?? 'vertical')) || formLayoutOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('formlayout', v?.value ?? 'vertical', selectChild)}
                  />
                  <RhyBusInputText
                    id="date-mindate"
                    label="Min Date"
                    type="date"
                    value={(selectedChild as any)?.minDate ?? ''}
                    error={!!(selectedChild as any)?.minDate && !!(selectedChild as any)?.maxDate && (selectedChild as any)?.minDate >= (selectedChild as any)?.maxDate}
                    errorMessage="Min Date must be before Max Date"
                    onChange={(e: any) => handleCbStyleChange('minDate', e.target.value || undefined, selectChild)}
                  />
                  <RhyBusInputText
                    id="date-maxdate"
                    label="Max Date"
                    type="date"
                    value={(selectedChild as any)?.maxDate ?? ''}
                    error={!!(selectedChild as any)?.minDate && !!(selectedChild as any)?.maxDate && (selectedChild as any)?.maxDate <= (selectedChild as any)?.minDate}
                    errorMessage="Max Date must be after Min Date"
                    onChange={(e: any) => handleCbStyleChange('maxDate', e.target.value || undefined, selectChild)}
                  />
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id="date-isbadge"
                      name="isBadge"
                      type="checkbox"
                      label="Is Badge"
                      checked={!!(selectedChild as any)?.isBadge}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        handleMultiCbStyleChange({ isBadge: checked, inline: checked, inlineType: checked ? ((selectedChild as any)?.inlineType || 'pill') : undefined, maxCount: checked ? (selectedChild as any)?.maxCount : undefined }, selectChild)
                      }}
                    />
                  </div>
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusCustomSelect
                      id="date-inlinetype"
                      label="Badge Style"
                      options={inlineTypeOptions}
                      value={inlineTypeOptions.find(o => {
                        if (!(selectedChild as any)?.inline) return o.value === 'sup'
                        return o.value === ((selectedChild as any)?.inlineType ?? 'pill')
                      }) || inlineTypeOptions[0]}
                      onChange={(v: any) => {
                        const val = v?.value ?? 'pill'
                        handleMultiCbStyleChange({ inlineType: val === 'sup' ? undefined : val, inline: val !== 'sup' }, selectChild)
                      }}
                    />
                  )}
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusInputText
                      id="date-maxcount"
                      label="Max Count"
                      type="number"
                      placeholder="e.g. 100"
                      value={String((selectedChild as any)?.maxCount ?? '')}
                      onChange={(e: any) => handleCbStyleChange('maxCount', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                    />
                  )}
                </>
              )}
              {isTextarea && (
                <>
                  <RhyBusInputText
                    id="textarea-rows"
                    label="Rows"
                    type="number"
                    placeholder="e.g. 5"
                    value={String((selectedChild as any)?.rows ?? '')}
                    onChange={(e: any) => handleCbStyleChange('rows', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                  />
                  <RhyBusCustomSelect
                    id="textarea-formlayout"
                    label="Form Layout"
                    options={formLayoutOptions}
                    value={formLayoutOptions.find(o => o.value === ((selectedChild as any)?.formlayout ?? 'vertical')) || formLayoutOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('formlayout', v?.value ?? 'vertical', selectChild)}
                  />
                  <RhyBusInputText
                    id="textarea-maxlength"
                    label="Max Length"
                    type="number"
                    placeholder="e.g. 500"
                    value={String((selectedChild as any)?.maxLength ?? '')}
                    onChange={(e: any) => handleCbStyleChange('maxLength', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                  />
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id="textarea-disabled"
                      name="disabled"
                      type="checkbox"
                      label="Disabled"
                      checked={!!(selectedChild as any)?.disabled}
                      onChange={(e: any) => handleCbStyleChange('disabled', e.target.checked, selectChild)}
                    />
                    <RhyBusInputCheck
                      id="textarea-isbadge"
                      name="isBadge"
                      type="checkbox"
                      label="Is Badge"
                      checked={!!(selectedChild as any)?.isBadge}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        handleMultiCbStyleChange({ isBadge: checked, inline: checked, inlineType: checked ? ((selectedChild as any)?.inlineType || 'pill') : undefined, maxCount: checked ? (selectedChild as any)?.maxCount : undefined }, selectChild)
                      }}
                    />
                  </div>
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusCustomSelect
                      id="textarea-inlinetype"
                      label="Badge Style"
                      options={inlineTypeOptions}
                      value={inlineTypeOptions.find(o => {
                        if (!(selectedChild as any)?.inline) return o.value === 'sup'
                        return o.value === ((selectedChild as any)?.inlineType ?? 'pill')
                      }) || inlineTypeOptions[0]}
                      onChange={(v: any) => {
                        const val = v?.value ?? 'pill'
                        handleMultiCbStyleChange({ inlineType: val === 'sup' ? undefined : val, inline: val !== 'sup' }, selectChild)
                      }}
                    />
                  )}
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusInputText
                      id="textarea-maxcount"
                      label="Max Count"
                      type="number"
                      placeholder="e.g. 100"
                      value={String((selectedChild as any)?.maxCount ?? '')}
                      onChange={(e: any) => handleCbStyleChange('maxCount', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                    />
                  )}
                </>
              )}
              {showType && (
                <RhyBusCustomSelect
                  id="type"
                  label="Type"
                  options={
                    isTextLike
                      ? typeOptions.filter((o: any) => ['text', 'email', 'number', 'phone', 'search', 'currency'].includes(o.value))
                      : typeOptions.filter((o: any) => ['radio', 'checkbox'].includes(o.value))
                  }
                  value={typeOptions.find((o: any) => {
                    const t = selectedChild?.type
                    return o.value === (t === 'Input' ? 'text' : t)
                  }) || null}
                  onChange={(e: any) => onchangeType(e, selectChild)}
                />
              )}
              {isTextLike && (
                <>
                  <RhyBusCustomSelect
                    id="input-formlayout"
                    label="Form Layout"
                    options={formLayoutOptions}
                    value={formLayoutOptions.find(o => o.value === ((selectedChild as any)?.formlayout ?? 'vertical')) || formLayoutOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('formlayout', v?.value ?? 'vertical', selectChild)}
                  />
                  <RhyBusInputText
                    id="input-maxlength"
                    label="Max Length"
                    type="number"
                    placeholder="e.g. 255"
                    value={String((selectedChild as any)?.maxLength ?? '')}
                    onChange={(e: any) => handleCbStyleChange('maxLength', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                  />
                  {['number', 'currency'].includes(selectedChild?.type ?? '') && (
                    <>
                      <RhyBusInputText
                        id="input-min"
                        label="Min"
                        type="number"
                        placeholder="Min value"
                        value={String((selectedChild as any)?.min ?? '')}
                        error={(selectedChild as any)?.min != null && (selectedChild as any)?.max != null && (selectedChild as any)?.min >= (selectedChild as any)?.max}
                        errorMessage="Min must be less than Max"
                        onChange={(e: any) => handleCbStyleChange('min', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                      />
                      <RhyBusInputText
                        id="input-max"
                        label="Max"
                        type="number"
                        placeholder="Max value"
                        value={String((selectedChild as any)?.max ?? '')}
                        error={(selectedChild as any)?.min != null && (selectedChild as any)?.max != null && (selectedChild as any)?.max <= (selectedChild as any)?.min}
                        errorMessage="Max must be greater than Min"
                        onChange={(e: any) => handleCbStyleChange('max', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                      />
                    </>
                  )}
                  {selectedChild?.type === 'currency' && (
                    <>
                      <RhyBusCustomSelect
                        id="input-currency"
                        label="Currency"
                        options={currencyOptions}
                        value={currencyOptions.find(o => o.value === ((selectedChild as any)?.currency ?? 'USD')) || currencyOptions[0]}
                        onChange={(v: any) => handleCbStyleChange('currency', v?.value ?? 'USD', selectChild)}
                      />
                      <RhyBusInputText
                        id="input-decimalplaces"
                        label="Decimal Places"
                        type="number"
                        placeholder="e.g. 2"
                        value={String((selectedChild as any)?.decimalPlaces ?? '')}
                        onChange={(e: any) => handleCbStyleChange('decimalPlaces', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                      />
                    </>
                  )}
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id="input-iconleft"
                      name="iconLeft"
                      type="checkbox"
                      label="Icon Left"
                      checked={!!(selectedChild as any)?.iconLeft}
                      onChange={(e: any) => handleCbStyleChange('iconLeft', e.target.checked, selectChild)}
                    />
                    <RhyBusInputCheck
                      id="input-iconright"
                      name="iconRight"
                      type="checkbox"
                      label="Icon Right"
                      checked={!!(selectedChild as any)?.iconRight}
                      onChange={(e: any) => handleCbStyleChange('iconRight', e.target.checked, selectChild)}
                    />
                  </div>
                  {(selectedChild as any)?.iconLeft && (
                    <IconPickerSelect
                      id="input-customiconleft"
                      label="Left Icon"
                      value={(selectedChild as any)?.customIconLeft || ''}
                      onChange={(v: string) => handleCbStyleChange('customIconLeft', v, selectChild)}
                    />
                  )}
                  {(selectedChild as any)?.iconRight && (
                    <IconPickerSelect
                      id="input-customiconright"
                      label="Right Icon"
                      value={(selectedChild as any)?.customIconRight || ''}
                      onChange={(v: string) => handleCbStyleChange('customIconRight', v, selectChild)}
                    />
                  )}
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id="input-isbadge"
                      name="isBadge"
                      type="checkbox"
                      label="Is Badge"
                      checked={!!(selectedChild as any)?.isBadge}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        handleMultiCbStyleChange({ isBadge: checked, inline: checked, inlineType: checked ? ((selectedChild as any)?.inlineType || 'pill') : undefined, maxCount: checked ? (selectedChild as any)?.maxCount : undefined }, selectChild)
                      }}
                    />
                    <RhyBusInputCheck
                      id="input-autocomplete"
                      name="autoComplete"
                      type="checkbox"
                      label="Auto Complete"
                      checked={!!(selectedChild as any)?.autoComplete}
                      onChange={(e: any) => handleCbStyleChange('autoComplete', e.target.checked, selectChild)}
                    />
                  </div>
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusCustomSelect
                      id="input-inlinetype"
                      label="Badge Style"
                      options={inlineTypeOptions}
                      value={inlineTypeOptions.find(o => {
                        if (!(selectedChild as any)?.inline) return o.value === 'sup'
                        return o.value === ((selectedChild as any)?.inlineType ?? 'pill')
                      }) || inlineTypeOptions[0]}
                      onChange={(v: any) => {
                        const val = v?.value ?? 'pill'
                        handleMultiCbStyleChange({ inlineType: val === 'sup' ? undefined : val, inline: val !== 'sup' }, selectChild)
                      }}
                    />
                  )}
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusInputText
                      id="input-maxcount"
                      label="Max Count"
                      type="number"
                      placeholder="e.g. 100"
                      value={String((selectedChild as any)?.maxCount ?? '')}
                      onChange={(e: any) => handleCbStyleChange('maxCount', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                    />
                  )}
                </>
              )}
              {showIsInfo && (
                <RhyBusInputCheck
                  id='isinfo'
                  name='is Info'
                  className='editable-label'
                  label='Is Info?'
                  type='checkbox'
                  checked={selectedChild.isInfo || false}
                  onChange={(e: any) => handleCheckboxChange(e, selectChild)}
                />
              )}
              {showTooltip && (
                <RhyBusCustomSelect
                  id="tooltipdirection"
                  label="Tooltip direction"
                  options={tooltipDirection}
                  value={tooltipDirection.find(o => o.value === (selectedChild?.position || 'top')) || tooltipDirection[0]}
                  onChange={(e: any) => handlePositionChange(e, selectChild)}
                />
              )}
              {showAccess && (
                <div className='d-flex gap-2 flex-wrap'>
                  <RhyBusInputCheck
                    id='readonly'
                    name='access'
                    className='editable-label '
                    label='Read Only'
                    type='radio'
                    checked={selectedChild.readonly || false}
                    onChange={(e: any) => handlePropertyChange(e, selectChild, 'readonly')}
                  />
                  <RhyBusInputCheck
                    id='ReadandWrite'
                    name='access'
                    className='editable-label'
                    label='Read and Write'
                    type='radio'
                    checked={!selectedChild.readonly}
                    onChange={(e: any) => handlePropertyChange(e, selectChild, 'readandwrite')}
                  />
                </div>
              )}
              {showMandatory && (
                <RhyBusInputCheck
                  id='ismandatory'
                  name='Is Mandatory'
                  className='editable-label'
                  label='Is Mandatory?'
                  type='checkbox'
                  checked={selectedChild.isMandatory || false}
                  onChange={(e: any) => handleMandatoryCheckboxChange(e, selectChild)}
                />
              )}
              {isCheckRadio && (
                <>
                  <RhyBusCustomSelect
                    id='check-position'
                    label='Position'
                    options={buttonPositionOptions}
                    value={buttonPositionOptions.find(o => o.value === (selectedChild as any)?.checkPosition) || null}
                    onChange={(v: any) => handleCbStyleChange('checkPosition', v?.value || '', selectChild)}
                  />
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id="checkradio-isgroup"
                      name="isGroup"
                      type="checkbox"
                      label="Group Options"
                      checked={!!(selectedChild as any)?.isGroup}
                      onChange={(e: any) => {
                        handleMultiCbStyleChange({ isGroup: e.target.checked, options: e.target.checked ? (selectedChild?.options ?? []) : [] }, selectChild)
                      }}
                    />
                  </div>
                  {(selectedChild as any)?.isGroup && (
                    <div className="d-flex flex-column gap-1 mt-1">
                      <label className="">Options</label>
                      {(selectedChild.options ?? []).map((opt: StaticOption, idx: number) => (
                        <div key={idx} className="d-flex gap-1 align-items-center">
                          <RhyBusInputText
                            id={`checkradio-opt-${idx}`}
                            placeholder="Option label"
                            value={opt.label}
                            className="font-12"
                            onChange={(e: any) => handleOptionChange(selectChild, idx, 'label', e.target.value)}
                          />
                          <span
                            role="button"
                            className="icon-rb-trashbin color-danger font-14 cursor-pointer"
                            onClick={() => handleRemoveOption(selectChild, idx)}
                          />
                        </div>
                      ))}
                      <RhyBusButton
                        label="+ Add Option"
                        variant="secondary"
                        size="sm"
                        className="font-12 mt-1"
                        onClick={() => handleAddOption(selectChild)}
                      />
                    </div>
                  )}
                </>
              )}
              {isSwitch && (
                <>
                  <RhyBusCustomSelect
                    id="switch-position"
                    label="Alignment"
                    options={buttonPositionOptions}
                    value={buttonPositionOptions.find(o => o.value === (selectedChild as any)?.checkPosition) || null}
                    onChange={(v: any) => handleCbStyleChange('checkPosition', v?.value || '', selectChild)}
                    isClearable
                  />
                  <RhyBusInputText
                    id="switch-firstlabel"
                    label="First Label"
                    placeholder="e.g. Off"
                    value={(selectedChild as any)?.firstLabel || ''}
                    onChange={(e: any) => handleCbStyleChange('firstLabel', e.target.value, selectChild)}
                  />
                  <RhyBusInputText
                    id="switch-secondlabel"
                    label="Second Label"
                    placeholder="e.g. On"
                    value={(selectedChild as any)?.secondLabel || ''}
                    onChange={(e: any) => handleCbStyleChange('secondLabel', e.target.value, selectChild)}
                  />
                  <RhyBusCustomSelect
                    id="switch-size"
                    label="Size"
                    options={switchSizeOptions}
                    value={switchSizeOptions.find(o => o.value === (selectedChild as any)?.inputSize) || null}
                    onChange={(v: any) => handleCbStyleChange('inputSize', v?.value ?? '', selectChild)}
                    isClearable
                  />
                  <RhyBusCustomSelect
                    id="switch-formlayout"
                    label="Form Layout"
                    options={formLayoutOptions}
                    value={formLayoutOptions.find(o => o.value === ((selectedChild as any)?.formlayout ?? 'vertical')) || formLayoutOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('formlayout', v?.value ?? 'vertical', selectChild)}
                  />
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id="switch-isbadge"
                      name="isBadge"
                      type="checkbox"
                      label="Is Badge"
                      checked={!!(selectedChild as any)?.isBadge}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        handleMultiCbStyleChange({ isBadge: checked, inline: checked, inlineType: checked ? ((selectedChild as any)?.inlineType || 'pill') : undefined, maxCount: checked ? (selectedChild as any)?.maxCount : undefined }, selectChild)
                      }}
                    />
                    <RhyBusInputCheck
                      id="switch-disabled"
                      name="disabled"
                      type="checkbox"
                      label="Disabled"
                      checked={!!(selectedChild as any)?.disabled}
                      onChange={(e: any) => handleCbStyleChange('disabled', e.target.checked, selectChild)}
                    />
                  </div>
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusCustomSelect
                      id="switch-inlinetype"
                      label="Badge Style"
                      options={inlineTypeOptions}
                      value={inlineTypeOptions.find(o => {
                        if (!(selectedChild as any)?.inline) return o.value === 'sup'
                        return o.value === ((selectedChild as any)?.inlineType ?? 'pill')
                      }) || inlineTypeOptions[0]}
                      onChange={(v: any) => {
                        const val = v?.value ?? 'pill'
                        handleMultiCbStyleChange({ inlineType: val === 'sup' ? undefined : val, inline: val !== 'sup' }, selectChild)
                      }}
                    />
                  )}
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusInputText
                      id="switch-maxcount"
                      label="Max Count"
                      type="number"
                      placeholder="e.g. 100"
                      value={String((selectedChild as any)?.maxCount ?? '')}
                      onChange={(e: any) => handleCbStyleChange('maxCount', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                    />
                  )}
                </>
              )}
              {isUpload && (
                <>
                  <RhyBusInputText
                    id="upload-hint"
                    label="Hint"
                    placeholder="Auto-generated from accepted types & size"
                    value={(selectedChild as any)?.hint || ''}
                    onChange={(e: any) => handleCbStyleChange('hint', e.target.value, selectChild)}
                  />
                  <RhyBusInputText
                    id="upload-note"
                    label="Note"
                    placeholder="Screenshots need to be saved locally before uploading."
                    value={(selectedChild as any)?.note || ''}
                    onChange={(e: any) => handleCbStyleChange('note', e.target.value, selectChild)}
                  />
                  <RhyBusInputText
                    id="upload-chooselabel"
                    label="Choose File Label"
                    placeholder="e.g. Browse Files"
                    value={(selectedChild as any)?.chooseFileLabel || ''}
                    onChange={(e: any) => handleCbStyleChange('chooseFileLabel', e.target.value, selectChild)}
                  />
                  <RhyBusInputText
                    id="upload-maxfiles"
                    label="Max Files"
                    type="number"
                    placeholder="e.g. 5"
                    value={String((selectedChild as any)?.maxFiles ?? '')}
                    onChange={(e: any) => handleCbStyleChange('maxFiles', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                  />
                  <RhyBusInputText
                    id="upload-maxfilesize"
                    label="Max File Size (MB)"
                    type="number"
                    placeholder="e.g. 25"
                    value={String((selectedChild as any)?.maxFileSizeMB ?? '')}
                    onChange={(e: any) => handleCbStyleChange('maxFileSizeMB', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                  />
                  <RhyBusInputText
                    id="upload-acceptedtypes"
                    label="Accepted Types"
                    placeholder="e.g. .pdf,.docx,.png"
                    value={(selectedChild as any)?.acceptedTypes || ''}
                    onChange={(e: any) => handleCbStyleChange('acceptedTypes', e.target.value, selectChild)}
                  />
                  <RhyBusInputText
                    id="upload-removemessage"
                    label="Remove Message"
                    placeholder="e.g. Are you sure to remove?"
                    value={(selectedChild as any)?.removeMessage || ''}
                    onChange={(e: any) => handleCbStyleChange('removeMessage', e.target.value, selectChild)}
                  />
                  <RhyBusInputText
                    id="upload-replacemessage"
                    label="Replace Message"
                    placeholder="e.g. Replace the existing file?"
                    value={(selectedChild as any)?.replaceMessage || ''}
                    onChange={(e: any) => handleCbStyleChange('replaceMessage', e.target.value, selectChild)}
                  />
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id="upload-disabled"
                      name="disabled"
                      type="checkbox"
                      label="Disabled"
                      checked={!!(selectedChild as any)?.disabled}
                      onChange={(e: any) => handleCbStyleChange('disabled', e.target.checked, selectChild)}
                    />
                    <RhyBusInputCheck
                      id="upload-isbadge"
                      name="isBadge"
                      type="checkbox"
                      label="Is Badge"
                      checked={!!(selectedChild as any)?.isBadge}
                      onChange={(e: any) => {
                        const checked = e.target.checked
                        handleMultiCbStyleChange({ isBadge: checked, inline: checked, inlineType: checked ? ((selectedChild as any)?.inlineType || 'pill') : undefined, maxCount: checked ? (selectedChild as any)?.maxCount : undefined }, selectChild)
                      }}
                    />
                  </div>
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusCustomSelect
                      id="upload-inlinetype"
                      label="Badge Style"
                      options={inlineTypeOptions}
                      value={inlineTypeOptions.find(o => {
                        if (!(selectedChild as any)?.inline) return o.value === 'sup'
                        return o.value === ((selectedChild as any)?.inlineType ?? 'pill')
                      }) || inlineTypeOptions[0]}
                      onChange={(v: any) => {
                        const val = v?.value ?? 'pill'
                        handleMultiCbStyleChange({ inlineType: val === 'sup' ? undefined : val, inline: val !== 'sup' }, selectChild)
                      }}
                    />
                  )}
                  {(selectedChild as any)?.isBadge && (
                    <RhyBusInputText
                      id="upload-maxcount"
                      label="Max Count"
                      type="number"
                      placeholder="e.g. 100"
                      value={String((selectedChild as any)?.maxCount ?? '')}
                      onChange={(e: any) => handleCbStyleChange('maxCount', e.target.value === '' ? undefined : Number(e.target.value), selectChild)}
                    />
                  )}
                </>
              )}
              {isRichText && (
                <RhyBusInputText
                  id="richtext-hint"
                  label="Hint"
                  placeholder="e.g. Max 500 characters"
                  value={(selectedChild as any)?.hint || ''}
                  onChange={(e: any) => handleCbStyleChange('hint', e.target.value, selectChild)}
                />
              )}
              {isPeoplePicker && (
                <>
                  <RhyBusCustomSelect
                    id="pp-position"
                    label="Alignment"
                    options={buttonPositionOptions}
                    value={buttonPositionOptions.find(o => o.value === (selectedChild as any)?.checkPosition) || null}
                    onChange={(v: any) => handleCbStyleChange('checkPosition', v?.value || '', selectChild)}
                    isClearable
                  />
                  <RhyBusInputText
                    id="pp-displaytext"
                    label="Display Text"
                    placeholder="e.g. Search people..."
                    value={(selectedChild as any)?.displayText || ''}
                    onChange={(e: any) => handleCbStyleChange('displayText', e.target.value, selectChild)}
                  />
                  <RhyBusInputText
                    id="pp-placeholder"
                    label="Placeholder"
                    placeholder="e.g. Search people..."
                    value={(selectedChild as any)?.placeholder || ''}
                    onChange={(e: any) => handleCbStyleChange('placeholder', e.target.value, selectChild)}
                  />
                  <RhyBusCustomSelect
                    id="pp-iconposition"
                    label="Icon Position"
                    options={[{ value: 'left', label: 'Icon Left' }, { value: 'right', label: 'Icon Right' }]}
                    value={(selectedChild as any)?.iconLeft ? { value: 'left', label: 'Icon Left' } : (selectedChild as any)?.iconRight ? { value: 'right', label: 'Icon Right' } : null}
                    onChange={(v: any) => handleMultiCbStyleChange({ iconLeft: v?.value === 'left', iconRight: v?.value === 'right' }, selectChild)}
                    isClearable
                  />
                  {((selectedChild as any)?.iconLeft || (selectedChild as any)?.iconRight) && (
                    <IconPickerSelect
                      id="pp-customicon"
                      label="Icon"
                      value={(selectedChild as any)?.customIcon || ''}
                      onChange={(v: string) => handleCbStyleChange('customIcon', v, selectChild)}
                    />
                  )}
                </>
              )}
              {isButton && (
                <>
                  <RhyBusCustomSelect
                    id='btn-variant'
                    label='Variant'
                    options={buttonVariantOptions}
                    value={buttonVariantOptions.find(o => o.value === ((selectedChild as any)?.variant ?? 'primary')) || buttonVariantOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('variant', v?.value ?? 'primary', selectChild)}
                  />
                  <RhyBusCustomSelect
                    id='btn-color'
                    label='Color'
                    options={buttonColorOptions}
                    value={buttonColorOptions.find(o => o.value === (selectedChild as any)?.color) || null}
                    onChange={(v: any) => handleCbStyleChange('color', v?.value ?? '', selectChild)}
                    isClearable
                  />
                  <RhyBusCustomSelect
                    id='btn-size'
                    label='Size'
                    options={buttonSizeOptions}
                    value={buttonSizeOptions.find(o => o.value === ((selectedChild as any)?.size || 'default')) || buttonSizeOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('size', v?.value === 'default' ? '' : (v?.value ?? ''), selectChild)}
                  />
                  <RhyBusCustomSelect
                    id='btn-type'
                    label='Type'
                    options={buttonTypeOptions}
                    value={buttonTypeOptions.find(o => o.value === ((selectedChild as any)?.buttonType ?? 'button')) || buttonTypeOptions[0]}
                    onChange={(v: any) => handleCbStyleChange('buttonType', v?.value ?? 'button', selectChild)}
                  />
                  <RhyBusCustomSelect
                    id='btn-position'
                    label='Button Position'
                    options={buttonPositionOptions}
                    value={buttonPositionOptions.find(o => o.value === (selectedChild as any)?.buttonPosition) || null}
                    onChange={(v: any) => handleCbStyleChange('buttonPosition', v?.value || '', selectChild)}
                  />
                  <IconPickerSelect
                    id='btn-left-icon'
                    label='Left Icon'
                    value={(selectedChild as any)?.leftIcon || ''}
                    onChange={(v: string) => handleCbStyleChange('leftIcon', v, selectChild)}
                  />
                  <IconPickerSelect
                    id='btn-right-icon'
                    label='Right Icon'
                    value={(selectedChild as any)?.rightIcon || ''}
                    onChange={(v: string) => handleCbStyleChange('rightIcon', v, selectChild)}
                  />
                  <RhyBusInputText
                    id='btn-classname'
                    label='Button Class Name'
                    placeholder='e.g. w-100 mt-2'
                    value={(selectedChild as any)?.buttonClassName || ''}
                    onChange={(e: any) => handleCbStyleChange('buttonClassName', e.target.value, selectChild)}
                  />
                  <div className="d-flex gap-3 flex-wrap">
                    <RhyBusInputCheck
                      id='btn-pill'
                      name='pill'
                      type='checkbox'
                      label='Pill'
                      checked={!!(selectedChild as any)?.pill}
                      onChange={(e: any) => handleCbStyleChange('pill', e.target.checked, selectChild)}
                    />
                    <RhyBusInputCheck
                      id='btn-disabled'
                      name='disabled'
                      type='checkbox'
                      label='Disabled'
                      checked={!!(selectedChild as any)?.disabled}
                      onChange={(e: any) => handleCbStyleChange('disabled', e.target.checked, selectChild)}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {addradio === true && isCheckRadio && (
            <RhyBusButton
              label="Add Radio Field"
              className=""
            />
          )}

          <div className="d-flex flex-column gap-1 mt-2">
            {isDirty && applyStatus === 'idle' && (
              <span className="" style={{ color: '#E67E22' }}>
                ● Unsaved changes
              </span>
            )}
            <div className="d-flex justify-content-end gap-1 flex-wrap">
              <RhyBusButton
                label="Clear"
                variant="secondary"
                leftIcon='icon-rb-close font-10'
                size='md'
                className=""
                onClick={handleClear}
              />
              <RhyBusButton
                label={applyStatus === 'applied' ? 'Applied' : 'Apply'}
                leftIcon='icon-rb-checked font-11'
                size='md'
                className=""
                onClick={handleApply}
              />
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  )
}

export default ElementOptions
